import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Dropdown,
  Table,
  Pagination,
} from "react-bootstrap";
import { FaRedo, FaTrash } from "react-icons/fa";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import axios from "axios";

import InvoiceModal from "./invoiceModal.js";
import CreditMemo from "./creditMemo.js";
import Payment from "./payment.js";
import FilterModel from "./filterModel.js";
import { useCurrency } from '../../context/currencyContext';

const Posting = () => {
  const { formatAmount } = useCurrency();
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showFilterModel, setShowFilterModel] = useState(false);
  const [showCreditMemoModal, setShowCreditMemoModal] = useState(false);
  const [showReceivePaymentModal, setShowReceivePaymentModal] = useState(false);
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountOptions, setAccountOptions] = useState([]);
  const [filteredPostings, setFilteredPostings] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [userLabId, setUserLabId] = useState(null);

  // Get userLabId from localStorage on component mount
  useEffect(() => {
    const labId = localStorage.getItem('userLabId');
    if (labId) {
      setUserLabId(labId);
    } else {
      setError('User Lab ID not found. Please log in again.');
    }
  }, []);

  const formatInvoiceNumber = (number) => {
    if (!number) return "N/A";
    return String(number).padStart(6, '0');
  };

  const formatCreditMemoNumber = (number) => {
    if (!number) return "N/A";
    return String(number).replace('CR-', '');
  };

  // Function to check if an invoice belongs to the current lab
  const checkInvoiceOwnership = async (invoiceId, invoiceNumber) => {
    try {
      // Try to fetch the invoice by ID first
      if (invoiceId) {
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/invoices/invoice/${invoiceId}`);
        if (response.data && response.data.addedBy === userLabId) {
          return true;
        }
      }
      
      // If no ID or not found, try by invoice number
      if (invoiceNumber) {
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/invoices/invoice-number/${invoiceNumber}`);
        if (response.data && response.data.addedBy === userLabId) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!userLabId) {
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Step 1: Fetch all invoices that belong to this lab
        let invoices = [];
        let validInvoiceIds = new Set();
        let validInvoiceNumbers = new Set();
        
        try {
          const invoicesResponse = await axios.get("https://hms.backend.hiragroupcloud.com/api/invoices/invoices/all", {
            params: { labId: userLabId }
          });
          invoices = invoicesResponse.data;
          
          // Create sets of valid invoice references
          invoices.forEach(invoice => {
            if (invoice._id) {
              validInvoiceIds.add(invoice._id);
            }
            if (invoice.invoiceNumber) {
              validInvoiceNumbers.add(invoice.invoiceNumber);
            }
          });
        } catch (invoiceError) {
          setError("Failed to fetch invoices. Please try again.");
          setLoading(false);
          return;
        }

        // Step 2: Fetch all payments
        let allPayments = [];
        try {
          const paymentsResponse = await axios.get("https://hms.backend.hiragroupcloud.com/api/receive-payments");
          allPayments = paymentsResponse.data;
        } catch (paymentError) {
          // Silently handle payment fetch error
        }

        // Step 3: Filter payments based on invoice ownership
        const validPayments = [];
        for (const payment of allPayments) {
          let isValid = false;
          
          // Check applied invoices
          if (payment.appliedInvoices && payment.appliedInvoices.length > 0) {
            for (const appliedInvoice of payment.appliedInvoices) {
              const invoiceId = appliedInvoice.invoiceId;
              const invoiceNumber = appliedInvoice.invoiceNumber;
              
              if ((invoiceId && validInvoiceIds.has(invoiceId)) || 
                  (invoiceNumber && validInvoiceNumbers.has(invoiceNumber))) {
                isValid = true;
                break;
              }
            }
          }
          
          // Check direct references
          if (!isValid && (payment.invoiceId || payment.invoiceNumber)) {
            if ((payment.invoiceId && validInvoiceIds.has(payment.invoiceId)) ||
                (payment.invoiceNumber && validInvoiceNumbers.has(payment.invoiceNumber))) {
              isValid = true;
            }
          }
          
          if (isValid) {
            validPayments.push(payment);
          }
        }

        // Step 4: Fetch all credit memos
        let allCreditMemos = [];
        try {
          const creditMemosResponse = await axios.get("https://hms.backend.hiragroupcloud.com/api/credit-memos");
          
          // Handle different response structures
          if (creditMemosResponse.data && Array.isArray(creditMemosResponse.data)) {
            allCreditMemos = creditMemosResponse.data;
          } else if (creditMemosResponse.data && creditMemosResponse.data.data && Array.isArray(creditMemosResponse.data.data)) {
            allCreditMemos = creditMemosResponse.data.data;
          } else if (creditMemosResponse.data && creditMemosResponse.data.creditMemos && Array.isArray(creditMemosResponse.data.creditMemos)) {
            allCreditMemos = creditMemosResponse.data.creditMemos;
          }
        } catch (creditError) {
          // Silently handle credit memo fetch error
        }

        // Step 5: Filter credit memos based on invoice ownership
        const validCreditMemos = [];
        for (const creditMemo of allCreditMemos) {
          let isValid = false;
          
          // Check applied invoices
          if (creditMemo.appliedInvoices && creditMemo.appliedInvoices.length > 0) {
            for (const appliedInvoice of creditMemo.appliedInvoices) {
              const invoiceId = appliedInvoice.invoiceId;
              const invoiceNumber = appliedInvoice.invoiceNumber;
              
              if ((invoiceId && validInvoiceIds.has(invoiceId)) || 
                  (invoiceNumber && validInvoiceNumbers.has(invoiceNumber))) {
                isValid = true;
                break;
              }
            }
          }
          
          // Check direct references
          if (!isValid && (creditMemo.invoiceId || creditMemo.invoiceNumber)) {
            if ((creditMemo.invoiceId && validInvoiceIds.has(creditMemo.invoiceId)) ||
                (creditMemo.invoiceNumber && validInvoiceNumbers.has(creditMemo.invoiceNumber))) {
              isValid = true;
            }
          }
          
          if (isValid) {
            validCreditMemos.push(creditMemo);
          }
        }

        // Process Invoices
        const invoiceEntries = invoices.map((invoice) => {
          try {
            const invoiceDate = invoice.invoiceDate ? new Date(invoice.invoiceDate) : new Date();
            
            return {
              id: invoice._id,
              postDate: invoiceDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              }),
              type: "Debit",
              accountId: invoice.accountId || "N/A",
              accountName: invoice.accountHolder || 
                          (invoice.patientFirstName && invoice.patientLastName ? 
                           `${invoice.patientFirstName} ${invoice.patientLastName}` : 
                           "Unknown Account"),
              amount: parseFloat(invoice.total) || 0,
              referenceNo: invoice.invoiceNumber ? [invoice.invoiceNumber] : [],
              originalDate: invoiceDate,
              originalData: invoice
            };
          } catch (err) {
            return null;
          }
        }).filter(Boolean);

        // Process Payments
        const paymentEntries = validPayments.map((payment) => {
          try {
            const paymentDate = payment.paymentDate ? new Date(payment.paymentDate) : new Date();
            const totalAmount = payment.appliedInvoices?.reduce((sum, inv) => 
              sum + parseFloat(inv.appliedAmount || 0), 0) || 
              parseFloat(payment.amountReceived) || 0;
            
            // Get invoice numbers for reference
            const invoiceNumbers = [];
            if (payment.appliedInvoices && payment.appliedInvoices.length > 0) {
              payment.appliedInvoices.forEach(inv => {
                if (inv.invoiceNumber) {
                  invoiceNumbers.push(inv.invoiceNumber);
                }
              });
            }
            
            return {
              id: payment._id,
              postDate: paymentDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              }),
              type: "Payment",
              accountId: payment.accountId || "N/A",
              accountName: payment.accountName || "Unknown Account",
              amount: totalAmount,
              referenceNo: invoiceNumbers.length > 0 ? invoiceNumbers : (payment.referenceNo ? [payment.referenceNo] : []),
              originalDate: paymentDate,
              originalData: payment
            };
          } catch (err) {
            return null;
          }
        }).filter(Boolean);

        // Process Credit Memos
        const creditEntries = validCreditMemos.map((credit) => {
          try {
            const creditDate = credit.creditDate ? new Date(credit.creditDate) : 
                             (credit.createdAt ? new Date(credit.createdAt) : new Date());
            
            // Get applied invoices references
            const appliedInvoices = credit.appliedInvoices || [];
            const referenceNumbers = appliedInvoices.map(inv => inv.invoiceNumber).filter(Boolean);
            
            // If no applied invoices, use credit memo ID
            const references = referenceNumbers.length > 0 ? 
                              referenceNumbers : 
                              (credit.creditMemoId ? [credit.creditMemoId] : []);
            
            return {
              id: credit._id,
              creditMemoId: credit.creditMemoId,
              postDate: creditDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              }),
              type: "Credit",
              accountId: credit.accountId || "N/A",
              accountName: credit.accountName || "Unknown Account",
              amount: parseFloat(credit.amount) || 0,
              referenceNo: references,
              originalDate: creditDate,
              originalData: credit
            };
          } catch (err) {
            return null;
          }
        }).filter(Boolean);

        // Combine all entries
        const allEntries = [...invoiceEntries, ...paymentEntries, ...creditEntries];
        
        // Sort by date (newest first)
        allEntries.sort((a, b) => b.originalDate - a.originalDate);

        setPostings(allEntries);
        setFilteredPostings(allEntries);

        // Get unique accounts for filter
        const uniqueAccounts = [...new Set(allEntries
          .map(item => item.accountName)
          .filter(name => name && name !== "Unknown Account" && name !== "N/A")
        )];
        
        setAccountOptions(['All', ...uniqueAccounts]);
        
      } catch (error) {
        setError("Failed to load posting entries.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userLabId]);

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
    let filtered = [...postings];

    if (filterData.startDate && filterData.endDate) {
      const start = new Date(filterData.startDate);
      const end = new Date(filterData.endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => {
        const itemDate = item.originalDate;
        return itemDate >= start && itemDate <= end;
      });
    }

    if (filterData.type && filterData.type !== 'All') {
      filtered = filtered.filter(item => item.type === filterData.type);
    }

    if (filterData.accounts && filterData.accounts.length > 0) {
      if (!filterData.accounts.includes('All')) {
        filtered = filtered.filter(item =>
          filterData.accounts.includes(item.accountName)
        );
      }
    }

    setFilteredPostings(filtered);
    setCurrentPage(1);
  };

  const handleResetFilter = () => {
    setFilters({});
    setFilteredPostings(postings);
    setCurrentPage(1);
  };

  const handleShowInvoiceModal = () => setShowInvoiceModal(true);
  const handleCloseInvoiceModal = () => setShowInvoiceModal(false);
  const handleShowFilterModel = () => setShowFilterModel(true);
  const handleCloseFilterModel = () => setShowFilterModel(false);
  const handleShowCreditMemoModal = () => setShowCreditMemoModal(true);
  const handleCloseCreditMemoModal = () => setShowCreditMemoModal(false);
  const handleShowReceivePaymentModal = () => setShowReceivePaymentModal(true);
  const handleCloseReceivePaymentModal = () => setShowReceivePaymentModal(false);

  const renderReference = (posting) => {
    try {
      if (posting.type === "Credit") {
        const creditMemoId = posting.referenceNo?.[0] || posting.creditMemoId || posting.originalData?.creditMemoId;
        const formatted = formatCreditMemoNumber(creditMemoId);
        
        if (formatted && formatted !== "N/A") {
          return (
            <Link
              to={`/credits/${posting.id}`}
              style={{
                textDecoration: "none",
                color: "#007bff",
                marginRight: "5px",
              }}
            >
              {formatted}
            </Link>
          );
        }
        return formatted || "N/A";
      }
      
      const refs = posting.referenceNo || [];
      if (refs.length === 0) return "N/A";

      const formattedLinks = refs.map((ref, index) => {
        const formatted = formatInvoiceNumber(ref);
        
        if (posting.type === "Payment") {
          return (
            <Link
              key={`${ref}-${index}`}
              to={`/payments/${posting.id}`}
              style={{
                textDecoration: "none",
                color: "#007bff",
                marginRight: "5px",
              }}
            >
              {formatted}
            </Link>
          );
        } else if (posting.type === "Debit") {
          return (
            <span key={`${ref}-${index}`} style={{ marginRight: "5px" }}>
              {formatted}
            </span>
          );
        }
        return null;
      }).filter(Boolean);

      if (formattedLinks.length === 0) return "N/A";

      let prefix = "";
      if (posting.type === "Payment") {
        prefix = "Invoices: ";
      }

      if (posting.type === "Payment" && formattedLinks.length > 1) {
        return (
          <>
            {prefix}
            {formattedLinks.map((link, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 ? ", " : ""}
                {link}
              </React.Fragment>
            ))}
          </>
        );
      }

      return <>{prefix}{formattedLinks[0]}</>;
    } catch (err) {
      return "N/A";
    }
  };

  const renderTypeCell = (posting) => {
    try {
      if (posting.type === "Payment") {
        return (
          <Link
            to={`/payments/${posting.id}`}
            style={{
              textDecoration: "none",
              color: "#007bff",
              fontWeight: "500",
            }}
          >
            {posting.type}
          </Link>
        );
      } else if (posting.type === "Credit") {
        return (
          <Link
            to={`/credits/${posting.id}`}
            style={{
              textDecoration: "none",
              color: "#007bff",
              fontWeight: "500",
            }}
          >
            {posting.type}
          </Link>
        );
      } else {
        return (
          <span style={{ color: "#333", fontWeight: "500" }}>
            {posting.type}
          </span>
        );
      }
    } catch (err) {
      return <span>Error</span>;
    }
  };
  
  const renderActionCell = (posting) => {
    if (hoveredRowId !== posting.id) return null;

    if (posting.type === "Payment" || posting.type === "Credit") {
      return (
        <FaTrash
          onClick={() => handleDelete(posting)}
          style={{
            color: "#dc3545",
            cursor: "pointer",
            fontSize: "16px",
          }}
          title="Delete"
        />
      );
    }
    return null;
  };

  const handleDelete = async (posting) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this ${posting.type}?`
    );
    if (!confirmDelete) return;

    try {
      let url = "";

      if (posting.type === "Payment") {
        url = `https://hms.backend.hiragroupcloud.com/api/payments/${posting.id}`;
      } else if (posting.type === "Credit") {
        url = `https://hms.backend.hiragroupcloud.com/api/credit-memos/${posting.id}`;
      } else {
        alert("Delete not supported for this type yet.");
        return;
      }

      await axios.delete(url);

      setPostings((prev) => prev.filter((item) => item.id !== posting.id));
      setFilteredPostings((prev) => prev.filter((item) => item.id !== posting.id));

      alert(`${posting.type} deleted successfully ✅`);
    } catch (error) {
      alert(`Failed to delete ${posting.type} ❌`);
    }
  };

  const cellStyle = {
    textAlign: "left",
    borderBottom: "1px solid #ddd",
    color: "#333",
  };

  const linkStyle = {
    textDecoration: "none",
    color: "#004aad",
    marginRight: "5px",
  };

  const entriesPerPage = 50;
  const totalPages = Math.ceil(filteredPostings.length / entriesPerPage);
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredPostings.slice(indexOfFirstEntry, indexOfLastEntry);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h4>Loading posting entries...</h4>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!userLabId) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h4>Please log in to view posting entries</h4>
      </div>
    );
  }

  return (
    <>
      <br />
      <div style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
        <div className="row">
          <div
            className="col-md-2"
            style={{ borderRight: "1px solid #004aad" }}
          >
            <h2 style={{ marginLeft: "30px" }}>Finances</h2>
            <div>
              <div style={{ marginBottom: "10px" }}>
                <Link
                  to="/posting"
                  style={{
                    textDecoration: "none",
                    color: "#004aad",
                    fontWeight: "520",
                    marginBottom: "10px",
                    marginLeft: "40px",
                  }}
                >
                  Postings
                </Link>
              </div>
              <div>
                <Link
                  to="/statement"
                  style={{
                    textDecoration: "none",
                    color: "#004aad",
                    fontWeight: "520",
                    marginBottom: "10px",
                    marginLeft: "40px",
                  }}
                >
                  Statements
                </Link>
              </div>
            </div>
          </div>
          <div className="col-md-10">
            <h2>Posting Entries</h2>
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            
            <div
              style={{
                backgroundColor: "#f4f4f4",
                padding: "20px",
                marginTop: "20px",
                borderTop: "2px solid #004aad",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <div>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    variant="primary"
                    id="filter_postings_button"
                    className="mr-2"
                    onClick={handleShowFilterModel}
                    style={{
                      backgroundColor: "#004aad",
                      color: "white",
                      borderColor: "#004aad",
                      marginRight: "10px",
                    }}
                  >
                    <FaRedo /> Filter
                  </Button>
                  <ButtonGroup>
                    <Dropdown>
                      <Dropdown.Toggle
                        style={{
                          backgroundColor: "#004aad",
                          color: "white",
                        }}
                        id="dropdown-basic"
                      >
                        Create New <span className="caret"></span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={handleShowReceivePaymentModal}>
                          Payment
                        </Dropdown.Item>
                        <Dropdown.Item onClick={handleShowInvoiceModal}>
                          Invoice
                        </Dropdown.Item>
                        <Dropdown.Item onClick={handleShowCreditMemoModal}>
                          Credit
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </ButtonGroup>
                </div>
              </div>

              <div
                style={{
                  marginBottom: "15px",
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, filteredPostings.length)} of {filteredPostings.length} entries
              </div>
            </div>
            <Table
              hover
              id="postings"
              style={{
                borderCollapse: "collapse",
                width: "100%",
              }}
            >
              <thead>
                <tr style={{ fontSize: "18px" }}>
                  {[
                    "Post Date",
                    "Type",
                    "Account",
                    "Amount",
                    "Reference",
                    "Statement Date",
                    "Action"
                  ].map((header, index) => (
                    <th
                      key={index}
                      style={{
                        backgroundColor: "#f4f4f4",
                        border: "none",
                        padding: "12px 16px",
                        textAlign: "left",
                        color: "#333",
                        fontWeight: "600",
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ backgroundColor: "#fff" }}>
                {currentEntries.length > 0 ? (
                  currentEntries.map((posting, index) => {
                    let displayAmount;
                    if (posting.type === "Debit") {
                      displayAmount = posting.amount || 0;
                    } else if (posting.type === "Credit") {
                      displayAmount = posting.amount || 0;
                    } else if (posting.type === "Payment") {
                      displayAmount = -(posting.amount || 0);
                    } else {
                      displayAmount = posting.amount || 0;
                    }

                    return (
                      <tr
                        key={`${posting.id}-${index}`}
                        onMouseEnter={() => setHoveredRowId(posting.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        style={{
                          backgroundColor: hoveredRowId === posting.id ? "#f9f9f9" : "#fff",
                          cursor: "pointer",
                        }}
                      >
                        <td style={cellStyle}>{posting.postDate}</td>
                        <td style={cellStyle}>
                          {renderTypeCell(posting)}
                        </td>
                        <td style={cellStyle}>
                          {posting.accountId && posting.accountId !== "N/A" ? (
                            <Link
                              to={`/tabsWorking/${posting.accountId}`}
                              style={linkStyle}
                            >
                              {posting.accountName}
                            </Link>
                          ) : (
                            <span>{posting.accountName}</span>
                          )}
                        </td>
                        <td style={cellStyle}>{formatAmount(displayAmount)}</td>
                        <td style={cellStyle}>
                          {renderReference(posting)}
                        </td>
                        <td style={cellStyle}>
                          {/* Empty Statement Date column for now */}
                        </td>
                        <td>{renderActionCell(posting)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                      {postings.length === 0 ? (
                        <div>
                          <h5>No posting entries found</h5>
                          <p className="text-muted">Try creating a new invoice, payment, or credit memo.</p>
                        </div>
                      ) : (
                        <div>
                          <h5>No entries match your filters</h5>
                          <Button 
                            variant="link" 
                            onClick={handleResetFilter}
                            style={{ color: "#004aad" }}
                          >
                            Clear filters to see all entries
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <div
              style={{
                backgroundColor: "#f4f4f4",
                padding: "20px",
                marginTop: "-20px",
                borderTop: "2px solid #004aad",
                color:"#333"
              }}
            >
              Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, filteredPostings.length)} of {filteredPostings.length} entries
            </div>
            {filteredPostings.length > entriesPerPage && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <Pagination>
                  <Pagination.Prev
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    <AiOutlineLeft />
                  </Pagination.Prev>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }
                    return pageNumber <= totalPages ? (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    ) : null;
                  })}

                  <Pagination.Next
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <AiOutlineRight />
                  </Pagination.Next>
                </Pagination>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          width: "95%",
          margin: "0 auto",
          padding: "0px",
          marginTop: "35%",
        }}
      >
      </div>

      <InvoiceModal
        show={showInvoiceModal}
        handleClose={handleCloseInvoiceModal}
      />
      <FilterModel
        show={showFilterModel}
        handleClose={handleCloseFilterModel}
        onFilterApply={handleFilterApply}
        accountOptions={accountOptions}
        typeOptions={['All', 'Debit', 'Payment', 'Credit']}
      />
      <CreditMemo
        show={showCreditMemoModal}
        handleClose={handleCloseCreditMemoModal}
      />
      <Payment
        show={showReceivePaymentModal}
        handleClose={handleCloseReceivePaymentModal}
      />
    </>
  );
};

export default Posting;